import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useListen, type ListenPhase } from './useListen';
import { useMyCallbackNumbers } from './useCallbackNumbers';
import type { CallbackChoice } from './types';

/**
 * SLICE 86 — the Listen confirm sheet (PLAN §4).
 *
 * 🔴 NEVER SHOWS SUCCESS BEFORE THE SERVER SAYS SO. The status line is driven by
 * `useListen().phase`; "Tracker accepted — calling {number} now" is reached ONLY
 * through `phase === 'accepted'`, which only a GET poll returning `accepted` can
 * produce. A 202 shows "Sent…", nothing more — we cannot observe the phone ring.
 *
 * The number picker defaults to the caller's LOGIN phone (no saved row — the POST
 * omits `callbackNumberId`), plus any saved numbers. `loginPhone` is passed in by
 * the control (read from the session) so this component is testable without an
 * `<AuthProvider>`.
 *
 * Raad Blue brand throughout — Listen is an ordinary product capability, not an
 * emergency; the SOS red (`--raad-sos`) is never used here.
 */
export function ListenDialog({
  vehicleId,
  vehicleName,
  loginPhone,
  onClose,
}: {
  vehicleId: string;
  vehicleName: string;
  /** The caller's login phone (the default call-back), or null if unknown. */
  loginPhone: string | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const listen = useListen(vehicleId);
  const saved = useMyCallbackNumbers();
  const panel = useRef<HTMLDivElement>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Escape-to-close + focus on open (same chrome as the driver/maintenance frames).
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);
  useEffect(() => {
    panel.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // The picker options: the login phone first (id = null), then saved numbers.
  const choices = useMemo<CallbackChoice[]>(() => {
    const list: CallbackChoice[] = [];
    if (loginPhone) list.push({ id: null, phone: loginPhone, label: null });
    for (const n of saved.data ?? []) list.push({ id: n.id, phone: n.phone, label: n.label });
    return list;
  }, [loginPhone, saved.data]);

  const selected = choices.find((c) => c.id === selectedId) ?? choices[0] ?? null;
  const selectedPhone = listen.callbackPhone ?? selected?.phone ?? '';

  const sending = listen.phase === 'sending';
  // A request is "in flight / resolved" once dispatched — then the picker is
  // replaced by the live status so the number can no longer change under it. An
  // ERROR keeps the picker so the caller can fix the number and retry.
  const dispatched =
    listen.phase === 'sent' ||
    listen.phase === 'accepted' ||
    listen.phase === 'notConfirmed' ||
    listen.phase === 'failed';

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
      <button
        type="button"
        aria-label={t('listen.cancel')}
        onClick={onClose}
        className="absolute inset-0 bg-black/60"
      />
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label={t('listen.confirm.title', { vehicle: vehicleName })}
        tabIndex={-1}
        data-testid="listen-dialog"
        className="relative max-h-[86vh] w-full overflow-y-auto rounded-t-panel border border-raad-border bg-raad-panel px-4 py-4 outline-none sm:max-w-md sm:rounded-panel"
      >
        <div className="flex items-start gap-3">
          <h2 className="flex-1 font-display text-base font-bold text-raad-text">
            {t('listen.confirm.title', { vehicle: vehicleName })}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('listen.cancel')}
            className="-mr-1 -mt-1 flex size-9 flex-none items-center justify-center rounded-lg text-raad-muted hover:text-raad-text"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <p className="mt-2 text-[13px] leading-relaxed text-raad-muted">
          {t('listen.confirm.body', { number: selectedPhone || t('listen.number.unknown') })}
        </p>

        {dispatched ? (
          <div className="mt-4">
            <StatusLine phase={listen.phase} number={selectedPhone} />
            <div className="mt-5 flex items-center justify-end">
              <button
                type="button"
                onClick={onClose}
                className="min-h-11 rounded-xl border border-raad-line-12 px-5 text-sm font-semibold text-raad-text transition-colors hover:border-raad-line-20"
              >
                {t('listen.close')}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="mt-4 flex flex-col gap-1.5">
              <label className="text-[12.5px] font-semibold text-raad-muted" htmlFor="listen-number">
                {t('listen.number.label')}
              </label>
              <select
                id="listen-number"
                data-testid="listen-number-picker"
                value={selectedId ?? ''}
                onChange={(e) => setSelectedId(e.target.value === '' ? null : e.target.value)}
                className="min-h-11 w-full rounded-xl border border-raad-line-8 bg-raad-input px-3.5 text-sm text-raad-text focus:border-raad-line-20 focus:outline-none"
              >
                {choices.map((c) => (
                  <option key={c.id ?? 'self'} value={c.id ?? ''}>
                    {c.id === null
                      ? t('listen.number.mine', { number: c.phone })
                      : c.label
                        ? `${c.label} · ${c.phone}`
                        : c.phone}
                  </option>
                ))}
              </select>
            </div>

            {/* An error from the POST keeps the picker so the number can be fixed
                and the call retried. Never optimistic in the other direction
                either: an error is an error until a new attempt succeeds. */}
            {listen.phase === 'error' ? (
              <p role="alert" data-testid="listen-status" className="mt-3 text-[13px] font-medium text-raad-error">
                {t(`listen.error.${listen.errorCode ?? 'unknown'}`)}
              </p>
            ) : null}

            <div className="mt-5 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="min-h-11 rounded-xl border border-raad-line-12 px-4 text-sm font-semibold text-raad-text transition-colors hover:border-raad-line-20"
              >
                {t('listen.cancel')}
              </button>
              <button
                type="button"
                data-testid="listen-call-cta"
                disabled={sending || choices.length === 0}
                onClick={() => listen.start(selectedId)}
                className="inline-flex min-h-11 items-center gap-2 rounded-xl bg-raad-primary px-5 text-sm font-bold text-raad-onprimary transition-colors hover:bg-raad-accent disabled:opacity-40"
              >
                {sending ? t('listen.state.sent') : t('listen.confirm.cta')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** The never-optimistic status line for a DISPATCHED request. `accepted` is the
 *  only "calling now" copy, and it is reached only from the server's poll. */
function StatusLine({
  phase,
  number,
}: {
  phase: ListenPhase;
  number: string;
}) {
  const { t } = useTranslation();

  if (phase === 'accepted') {
    return (
      <p role="status" data-testid="listen-status" className="text-sm font-semibold text-raad-accent">
        {t('listen.state.calling', { number })}
      </p>
    );
  }
  if (phase === 'notConfirmed') {
    return (
      <p role="status" data-testid="listen-status" className="text-sm font-medium text-raad-warn">
        {t('listen.state.notConfirmed')}
      </p>
    );
  }
  if (phase === 'failed') {
    return (
      <p role="alert" data-testid="listen-status" className="text-sm font-medium text-raad-error">
        {t('listen.error.failed')}
      </p>
    );
  }
  // 'sent' (and the transient 'sending' never reaches here).
  return (
    <p role="status" data-testid="listen-status" className="text-sm font-medium text-raad-muted">
      {t('listen.state.sent')}
    </p>
  );
}
